import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, FormControlLabel, FormHelperText, IconButton, MenuItem, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, TextFieldProps, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { clockToMinutes, humanize, minutesToClock, splitMinutes } from './templateDraft';
import { ObjectFieldSchema, PluginField } from './types';

interface ParamFieldProps {
  field: PluginField;
  value: unknown;
  overridden: boolean;
  error: string | null;
  onChange: (value: unknown) => void;
  onReset: () => void;
  /** Shown but not changeable, as on an older version of a template. */
  disabled?: boolean;
}

const DAY_MINUTES = 24 * 60;

const toNumber = (raw: string): number | undefined => (raw.trim() === '' ? undefined : Number(raw));
const toText = (raw: string): string | undefined => raw || undefined;

type EditableFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'onBlur'> & { value: unknown; parse: (raw: string) => unknown; onValue: (value: unknown) => void };

/** Emptying the box while retyping keeps it empty; only leaving it empty puts the default back. */
const EditableField = ({ value, parse, onValue, ...props }: EditableFieldProps) => {
  const shown = value === undefined || value === null ? '' : String(value);
  const [raw, setRaw] = useState(shown);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setRaw(shown);
  }, [shown, editing]);
  return (
    <TextField
      {...props}
      value={editing ? raw : shown}
      onFocus={() => {
        setEditing(true);
        setRaw(shown);
      }}
      onChange={(event) => {
        setRaw(event.target.value);
        if (event.target.value.trim() !== '') onValue(parse(event.target.value));
      }}
      onBlur={() => {
        setEditing(false);
        if (raw.trim() === '') onValue(undefined);
      }}
    />
  );
};

/** A number as the field shows it: hours and minutes for a duration, a clock for a time of day. */
const formatNumber = (field: PluginField, value: number): string => {
  if (field.type === 'duration') {
    const { hours, minutes } = splitMinutes(value);
    return `${hours}h ${minutes}m`;
  }
  if (field.type === 'time_of_day') return value >= DAY_MINUTES ? `${minutesToClock(value - DAY_MINUTES)} next day` : minutesToClock(value);
  return String(value);
};

const formatDefault = (field: PluginField): string => {
  const value = field.default;
  if (value === null || value === undefined || value === '') return 'empty';
  if (typeof value === 'number') return formatNumber(field, value);
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (typeof value === 'object') return 'see JSON';
  return String(value);
};

const rangeHint = (field: PluginField) => {
  const { min, max } = field.validation;
  if (min !== undefined && max !== undefined) return `${formatNumber(field, min)} to ${formatNumber(field, max)}`;
  if (min !== undefined) return `at least ${formatNumber(field, min)}`;
  if (max !== undefined) return `at most ${formatNumber(field, max)}`;
  return '';
};

/** A JSON editor for the values no built-in control fits; the value changes only once the text parses. */
const JsonInput = ({ value, onChange, label, disabled = false }: { value: unknown; onChange: (value: unknown) => void; label: string; disabled?: boolean }) => {
  const serialized = JSON.stringify(value ?? null, null, 2);
  const [text, setText] = useState(serialized);
  const [parseError, setParseError] = useState<string | null>(null);
  useEffect(() => {
    setText(serialized);
    setParseError(null);
  }, [serialized]);
  return (
    <TextField
      label={label}
      disabled={disabled}
      multiline
      minRows={3}
      maxRows={14}
      fullWidth
      size="small"
      value={text}
      error={parseError !== null}
      helperText={parseError ?? 'JSON'}
      inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
      onChange={(event) => {
        setText(event.target.value);
        try {
          const parsed = JSON.parse(event.target.value);
          setParseError(null);
          onChange(parsed);
        } catch {
          setParseError('Not valid JSON yet');
        }
      }}
    />
  );
};

const cellInput = (column: ObjectFieldSchema, cell: unknown, onCell: (value: unknown) => void, label: string, disabled: boolean) => {
  if (column.kind === 'boolean') return <Switch size="small" disabled={disabled} checked={cell === true} onChange={(event) => onCell(event.target.checked)} inputProps={{ 'aria-label': label }} />;
  if (column.options && column.options.length > 0) {
    return (
      <TextField select size="small" disabled={disabled} value={cell ?? ''} onChange={(event) => onCell(event.target.value)} inputProps={{ 'aria-label': label }} sx={{ minWidth: 110 }}>
        {column.options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      size="small"
      disabled={disabled}
      type={column.kind === 'number' ? 'number' : 'text'}
      value={cell ?? ''}
      placeholder={column.validation.required ? 'required' : 'optional'}
      inputProps={{ 'aria-label': label, min: column.validation.min, max: column.validation.max }}
      onChange={(event) => onCell(column.kind === 'number' ? toNumber(event.target.value) : event.target.value || undefined)}
      sx={{ minWidth: 90 }}
    />
  );
};

/** A list of rows with the same scalar columns, such as an overtime tier ladder. */
const RowsGrid = ({ columns, rows, onChange, label, disabled }: { columns: ObjectFieldSchema[]; rows: Record<string, unknown>[]; onChange: (rows: Record<string, unknown>[]) => void; label: string; disabled: boolean }) => {
  const setCell = (index: number, name: string, value: unknown) => {
    const next = rows.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const updated = { ...row, [name]: value };
      if (value === undefined) delete updated[name];
      return updated;
    });
    onChange(next);
  };
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.name}>
                {humanize(column.name)}
                {column.validation.required ? ' *' : ''}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key -- rows have no identity until the author names them
            <TableRow key={index}>
              {columns.map((column) => (
                <TableCell key={column.name}>{cellInput(column, row[column.name], (value) => setCell(index, column.name, value), `${label} row ${index + 1} ${column.name}`, disabled)}</TableCell>
              ))}
              <TableCell>
                <IconButton size="small" aria-label={`Remove row ${index + 1}`} disabled={disabled} onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button size="small" startIcon={<AddIcon />} disabled={disabled} onClick={() => onChange([...rows, {}])} sx={{ mt: 1 }}>
        Add row
      </Button>
    </Box>
  );
};

const ParamField = ({ field, value, overridden, error, onChange, onReset, disabled = false }: ParamFieldProps) => {
  const label = humanize(field.name);
  const hint = rangeHint(field);
  const helper = error ?? [hint, `default: ${formatDefault(field)}`].filter(Boolean).join(' · ');
  const options = field.options ?? field.validation.items?.options ?? [];

  let control: ReactNode;
  switch (field.type) {
    case 'switch':
      control = <FormControlLabel control={<Switch checked={value === true} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />} label={label} />;
      break;
    case 'segmented':
      control = (
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">
            {label}
          </Typography>
          <ToggleButtonGroup size="small" exclusive disabled={disabled} value={value ?? null} onChange={(_, next) => next !== null && onChange(next)} aria-label={label}>
            {options.map((option) => (
              <ToggleButton key={option} value={option}>
                {humanize(option)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      );
      break;
    case 'select':
      control = (
        <TextField select size="small" label={label} disabled={disabled} value={value ?? ''} onChange={(event) => onChange(event.target.value)} sx={{ minWidth: 220 }} error={error !== null}>
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {humanize(option)}
            </MenuItem>
          ))}
        </TextField>
      );
      break;
    case 'duration': {
      const minutes = typeof value === 'number' ? value : undefined;
      const parts = minutes !== undefined ? splitMinutes(minutes) : undefined;
      const update = (hours: number | undefined, rest: number | undefined) => onChange(hours === undefined && rest === undefined ? undefined : (hours ?? 0) * 60 + (rest ?? 0));
      control = (
        <Box>
          <Typography variant="caption" color="text.secondary" component="div">
            {label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              type="number"
              label="Hours"
              disabled={disabled}
              value={parts?.hours ?? ''}
              onChange={(event) => update(toNumber(event.target.value), parts?.minutes)}
              inputProps={{ min: 0, 'aria-label': `${label} hours` }}
              sx={{ width: 100 }}
              error={error !== null}
            />
            <TextField
              size="small"
              type="number"
              label="Minutes"
              disabled={disabled}
              value={parts?.minutes ?? ''}
              onChange={(event) => update(parts?.hours, toNumber(event.target.value))}
              inputProps={{ min: 0, max: 59, 'aria-label': `${label} minutes` }}
              sx={{ width: 100 }}
              error={error !== null}
            />
          </Box>
        </Box>
      );
      break;
    }
    case 'time_of_day': {
      // A bound past 24h means the time may fall on the next day: 05:00 next day is 1740.
      const spansMidnight = (field.validation.max ?? 0) >= DAY_MINUTES;
      const minutes = typeof value === 'number' ? value : undefined;
      const nextDay = minutes !== undefined && minutes >= DAY_MINUTES;
      const clock = minutes !== undefined ? minutesToClock(minutes % DAY_MINUTES) : '';
      const update = (time: number | undefined, onNextDay: boolean) => onChange(time === undefined ? undefined : time + (onNextDay ? DAY_MINUTES : 0));
      control = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <EditableField size="small" type="time" label={label} disabled={disabled} value={clock} parse={clockToMinutes} onValue={(time) => update(time as number | undefined, nextDay)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} error={error !== null} />
          {spansMidnight && <FormControlLabel control={<Switch size="small" checked={nextDay} disabled={disabled || minutes === undefined} onChange={(event) => update(minutes !== undefined ? minutes % DAY_MINUTES : undefined, event.target.checked)} />} label="Next day" />}
        </Box>
      );
      break;
    }
    case 'date':
      control = <TextField size="small" type="date" label={label} disabled={disabled} value={value ?? ''} onChange={(event) => onChange(event.target.value || undefined)} InputLabelProps={{ shrink: true }} sx={{ width: 190 }} error={error !== null} />;
      break;
    case 'month_day':
      control = <EditableField size="small" label={label} disabled={disabled} placeholder="MM-DD" value={value} parse={toText} onValue={onChange} sx={{ width: 160 }} error={error !== null} />;
      break;
    case 'number':
      control = <EditableField size="small" type="number" label={label} disabled={disabled} value={value} parse={toNumber} onValue={onChange} inputProps={{ min: field.validation.min, max: field.validation.max }} sx={{ width: 200 }} error={error !== null} />;
      break;
    case 'text':
      control = <EditableField size="small" label={label} disabled={disabled} value={value} parse={toText} onValue={onChange} sx={{ minWidth: 280 }} error={error !== null} />;
      break;
    default:
      if (field.kind === 'array' && field.validation.items?.kind === 'object' && field.validation.items.fields) {
        control = (
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              {label}
            </Typography>
            <RowsGrid columns={field.validation.items.fields} rows={Array.isArray(value) ? (value as Record<string, unknown>[]) : []} onChange={onChange} label={label} disabled={disabled} />
          </Box>
        );
      } else if (field.kind === 'number') {
        control = <EditableField size="small" type="number" label={label} disabled={disabled} value={value} parse={toNumber} onValue={onChange} sx={{ width: 200 }} error={error !== null} />;
      } else if (field.kind === 'string') {
        control = <EditableField size="small" label={label} disabled={disabled} value={value} parse={toText} onValue={onChange} sx={{ minWidth: 280 }} error={error !== null} />;
      } else if (field.kind === 'boolean') {
        control = <FormControlLabel control={<Switch checked={value === true} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />} label={label} />;
      } else {
        control = <JsonInput value={value} onChange={onChange} label={label} disabled={disabled} />;
      }
  }

  return (
    <Box sx={{ py: 1 }} data-testid={`param-${field.name}`}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{control}</Box>
        <Tooltip title={overridden ? 'Back to the plugin default' : 'Uses the plugin default'}>
          <span>
            <IconButton size="small" aria-label={`Reset ${label}`} disabled={disabled || !overridden} onClick={onReset} color={overridden ? 'primary' : 'default'}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <FormHelperText error={error !== null}>{helper}</FormHelperText>
    </Box>
  );
};

export default ParamField;
